import { ENDPOINT_DEMO_EMR_ANALYSIS } from "@constants/urls";
import { create } from "zustand";
import useSessionStore from "../../hooks/useSessionStore";
import type { EMRData, UserEMRData } from "./types";

type EMRStore = {
	goldStandardData: Partial<EMRData>;
	userData: Partial<UserEMRData>;
	currentItemGoldStandard: Partial<EMRData>;
	submitUserData: (input: string) => Promise<void>;
	resetUserData: () => void;
	updateFromSessionData: (currentItemIndex: number) => void;
	isSubmittingEMRData: boolean;
};

export const defaultGoldStandardData: Partial<EMRData> = {
	chiefComplaint:
		"My chest hurts when I breathe in and I can't catch my breath.",
	vitals: {
		HR: 115,
		BP: "135/85",
		RR: 24,
		SpO2: 88,
		Temp: 37.2,
	},
};

export const useEMRStore = create<EMRStore>((set) => ({
	goldStandardData: defaultGoldStandardData,
	userData: defaultGoldStandardData,
	currentItemGoldStandard: {},
	isSubmittingEMRData: false,
	updateFromSessionData: (currentItemIndex: number) => {
		const sessionItems = useSessionStore.getState().sessionItems;
		const currentItem = sessionItems[currentItemIndex];

		const response = currentItem?.response as {
			goldStandard?: Partial<EMRData>;
		};

		if (!response?.goldStandard) {
			set({
				currentItemGoldStandard: {},
			});
			return;
		}

		const newGoldStandard = response.goldStandard;

		set((state) => ({
			goldStandardData: {
				...state.goldStandardData,
				...newGoldStandard,
			},
			currentItemGoldStandard: newGoldStandard,
		}));
	},

	submitUserData: async (input: string) => {
		try {
			set({ isSubmittingEMRData: true });
			const response = await fetch(ENDPOINT_DEMO_EMR_ANALYSIS, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					agentId: 741,
					input,
				}),
			});

			const apiResponse = await response.json();
			const finalResponse = JSON.parse(apiResponse);

			set((state) => {
				const updatedUserData = { ...state.userData };

				for (const key of Object.keys(state.currentItemGoldStandard)) {
					if (key in finalResponse) {
						(updatedUserData as Record<string, unknown>)[key] =
							finalResponse[key];
					}
				}

				return {
					userData: updatedUserData,
					isSubmittingEMRData: false,
				};
			});
		} catch (error) {
			console.error("API call failed:", error);
		}
	},

	resetUserData: () => {
		set({
			userData: {},
			goldStandardData: {},
			isSubmittingEMRData: false,
		});
	},
}));
